import React, { useState } from 'react';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { Tom, Ken } from './Characters';
import './DelaySavingCost.scss';

const START_AGE = 20;
const END_AGE = 56;
const span = (from: number, to: number): React.CSSProperties => ({
  left: `${((from - START_AGE) / (END_AGE - START_AGE)) * 100}%`,
  width: `${((to - from) / (END_AGE - START_AGE)) * 100}%`
});
const at = (age: number): React.CSSProperties => ({ left: `${((age - START_AGE) / (END_AGE - START_AGE)) * 100}%` });
const AGES = [20, 22, 37, 56];
// Put 22 below Ken's row: it is too close to 20 to share the upper axis.
const TOP_AGES = [20, 37, 56];
const COLUMNS = [
  { key: 'principal', Icon: SavingsOutlinedIcon },
  { key: 'period', Icon: CalendarMonthOutlinedIcon },
  { key: 'return', Icon: TrendingUpRoundedIcon }
];
// Keep the source illustration's rounded figures and inclusive contribution-year counts.
const ROWS = [
  { who: 'tom', name: 'Tom', Character: Tom, principal: 216, period: 18, calc: '$10,000 × 12 × 18', rate: '1,274%' },
  { who: 'ken', name: 'Ken', Character: Ken, principal: 420, period: 35, calc: '$10,000 × 12 × 35', rate: '606%' }
];

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const DelaySavingCost = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [metric, setMetric] = useState<'principal' | 'period'>('principal');
  const [animationRun, setAnimationRun] = useState(0);
  const maximum = Math.max(...ROWS.map(row => row[metric]));

  return (
    <article className="delay-saving-cost" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="dsc-head">
        <h2 className="dsc-title">
          <span>{t('dsc-title')}</span>
        </h2>
        <p className="dsc-hook">{t('dsc-hook')}</p>
      </header>

      <section className="dsc-cast" aria-label={t('dsc-people')}>
        {ROWS.map(({ who, name, Character }, index) => (
          <div key={who} className={`dsc-person is-${who}`}>
            <div className="dsc-portrait">
              <Character size={104} />
            </div>
            <div className="dsc-person-copy">
              <strong>{name}</strong>
              <span className="dsc-start-label">{t('dsc-start-age', { age: index === 0 ? 20 : 22 })}</span>
              <p>{t(`dsc-${who}-plan`)}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="dsc-timeline-section" aria-label={t('dsc-timeline-title')}>
        <div className="dsc-section-header">
          <h3 className="dsc-section-title">
            <span>01</span>
            {t('dsc-timeline-title')}
          </h3>
          <button
            className="dsc-replay"
            type="button"
            aria-label={t('dsc-replay-label')}
            onClick={() => setAnimationRun(run => run + 1)}
          >
            <ReplayRoundedIcon aria-hidden="true" />
            {t('dsc-replay')}
          </button>
        </div>
        <div className="dsc-legend">
          <span>
            <i className="is-paying" />
            {t('dsc-saving-legend')}
          </span>
          <span>
            <i className="is-growing" />
            {t('dsc-growing-legend')}
          </span>
          <span>
            <i className="is-waiting" />
            {t('dsc-waiting-legend')}
          </span>
        </div>
        <div className="dsc-timeline-viewport" tabIndex={0} role="region" aria-label={t('dsc-timeline-title')}>
          <div className="dsc-timeline" key={animationRun}>
            <div className="dsc-axis">
              {TOP_AGES.map(age => (
                <span key={age} className="dsc-age" style={at(age)}>
                  {t('dsc-age', { age })}
                </span>
              ))}
            </div>
            <div className="dsc-row is-tom">
              <span className="dsc-who">
                <Tom size={44} />
                Tom
              </span>
              <div className="dsc-track">
                <span className="dsc-bar is-paying" style={span(20, 37)}>
                  <SavingsOutlinedIcon aria-hidden="true" />
                  {t('dsc-tom-pay')}
                </span>
                <span className="dsc-bar is-stopped" style={span(37, 56)}>
                  <TrendingUpRoundedIcon aria-hidden="true" />
                  <span>
                    {t('dsc-tom-stop')}
                    <small>{t('dsc-keep-growing')}</small>
                  </span>
                </span>
              </div>
            </div>
            <div className="dsc-row is-ken">
              <span className="dsc-who">
                <Ken size={44} />
                Ken
              </span>
              <div className="dsc-track">
                <span className="dsc-bar is-enjoying" style={span(20, 22)} title={t('dsc-ken-enjoy')}>
                  <span className="dsc-wait-dot" aria-hidden="true" />
                </span>
                <span className="dsc-bar is-paying" style={span(22, 56)}>
                  <SavingsOutlinedIcon aria-hidden="true" />
                  {t('dsc-ken-pay')}
                </span>
              </div>
            </div>
            <div className="dsc-axis is-under">
              <span className="dsc-age is-ken-start" style={at(22)}>
                {t('dsc-age', { age: 22 })}
              </span>
            </div>
            <div className="dsc-guides" aria-hidden="true">
              {AGES.map(age => (
                <span key={age} style={at(age)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="dsc-result">
        <span className="dsc-icon-badge">
          <FlagOutlinedIcon aria-hidden="true" />
        </span>
        <div className="dsc-result-copy">
          <p>{t('dsc-result')}</p>
          <strong>{t('dsc-result-amount')}</strong>
          <small>{t('dsc-assumption')}</small>
        </div>
      </section>

      <section className="dsc-comparison-section" aria-label={t('dsc-but')}>
        <h3 className="dsc-section-title">
          <span>02</span>
          {t('dsc-but')}
        </h3>
        <div className="dsc-metric-picker" role="group" aria-label={t('dsc-comparison-controls')}>
          {COLUMNS.slice(0, 2).map(({ key, Icon }) => (
            <button
              key={key}
              type="button"
              aria-pressed={metric === key}
              onClick={() => setMetric(key as 'principal' | 'period')}
            >
              <Icon aria-hidden="true" />
              {t(`dsc-${key}`)}
            </button>
          ))}
        </div>
        <div className="dsc-comparison" role="group" aria-label={t(`dsc-${metric}`)}>
          {ROWS.map(({ who, name, Character, ...values }) => (
            <div key={who} className={`dsc-comparison-row is-${who}`}>
              <div className="dsc-comparison-label">
                <Character size={42} />
                <strong>{name}</strong>
                <span>{t(`dsc-${who}-${metric}`)}</span>
              </div>
              <div className="dsc-comparison-track" aria-hidden="true">
                <span key={`${metric}-${animationRun}`} style={{ width: `${(values[metric] / maximum) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="dsc-insight">
          <span className="dsc-icon-badge">
            {metric === 'principal' ? (
              <SavingsOutlinedIcon aria-hidden="true" />
            ) : (
              <CalendarMonthOutlinedIcon aria-hidden="true" />
            )}
          </span>
          <div>
            <strong aria-live="polite" aria-atomic="true">
              {t(`dsc-extra-${metric}`)}
            </strong>
            <p>{t('dsc-insight')}</p>
          </div>
        </div>
        <details className="dsc-details">
          <summary>
            <TableChartOutlinedIcon aria-hidden="true" />
            {t('dsc-details')}
            <ExpandMoreRoundedIcon className="dsc-chevron" aria-hidden="true" />
          </summary>
          <p className="dsc-scroll-hint">{t('dsc-scroll-hint')}</p>
          <div className="dsc-table-viewport" tabIndex={0} role="region" aria-label={t('dsc-details')}>
            <table className="dsc-table">
              <thead>
                <tr>
                  <th aria-hidden="true" />
                  {COLUMNS.map(({ key, Icon }) => (
                    <th key={key} scope="col">
                      <Icon aria-hidden="true" />
                      <span>{t(`dsc-${key}`)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(({ who, name, Character, calc, rate }) => (
                  <tr key={who} className={`is-${who}`}>
                    <th scope="row">
                      <Character size={40} />
                      <span>{name}</span>
                    </th>
                    <td>
                      <strong>{t(`dsc-${who}-principal`)}</strong>
                      <small>({calc})</small>
                    </td>
                    <td>
                      <strong>{t(`dsc-${who}-period`)}</strong>
                    </td>
                    <td>
                      <strong>{rate}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </section>

      <div className="dsc-tip">
        <LightbulbOutlinedIcon aria-hidden="true" />
        <p>{t('dsc-tip')}</p>
      </div>
      <p className="dsc-disclaimer">
        {t('dsc-disclaimer-1')}
        <br />
        {t('dsc-disclaimer-2')}
      </p>
      <p className="dsc-risk">{t('dsc-risk')}</p>
    </article>
  );
};

export default DelaySavingCost;
